import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Ticket, RefreshCw, CalendarDays, ArrowRight } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { scrollToId } from "../lib/scroll";
import { API } from "../lib/api";

const statusStyle = (s) =>
  s === "Últimas entradas"
    ? "border-winebright/60 text-winebright bg-wine/20"
    : s === "Entradas disponibles"
    ? "border-gold/50 text-gold bg-gold/10"
    : "border-cream/20 text-sand bg-cream/5";

export const Concerts = () => {
  const [concerts, setConcerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/concerts`)
      .then((r) => setConcerts(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onTickets = (c) => {
    if (c.ticket_url) {
      window.open(c.ticket_url, "_blank");
    } else {
      toast.info("Entradas a la venta próximamente. Para reservas y asistencia, escríbenos desde Contratación.");
    }
  };

  return (
    <section id="conciertos" className="relative py-24 sm:py-32 bg-coal/40">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <SectionHeader
          eyebrow="Gira"
          title={
            <>
              Próximos <span className="italic text-gold">conciertos</span>.
            </>
          }
        />

        <div className="mt-14">
          {loading && (
            <div className="space-y-4" data-testid="concerts-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 bg-cream/[0.03] animate-pulse" />
              ))}
            </div>
          )}

          {error && (
            <div className="border border-wine/40 bg-wine/10 p-8 text-center" data-testid="concerts-error">
              <p className="text-sand">No se pudieron cargar los conciertos.</p>
              <button
                data-testid="concerts-retry-button"
                onClick={load}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-2.5 text-[12px] uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-ink transition-all"
              >
                <RefreshCw size={14} /> Reintentar
              </button>
            </div>
          )}

          {!loading && !error && concerts.length === 0 && (
            <Reveal>
              <div className="border border-gold/15 bg-card p-12 md:p-16 text-center" data-testid="concerts-pending">
                <CalendarDays className="mx-auto text-gold" size={42} strokeWidth={1.25} />
                <p className="mt-6 text-center font-serif text-3xl md:text-4xl text-cream">Calendario pendiente de confirmación de fechas</p>
                <p className="mx-auto mt-4 max-w-md text-sm md:text-base text-sand leading-relaxed text-justify">
                  Estamos preparando la nueva gira. Muy pronto anunciaremos aquí los próximos conciertos: síguenos en redes o escríbenos para llevar «Con un solo corazón» a tu localidad.
                </p>
                <button
                  data-testid="concerts-cta"
                  onClick={() => scrollToId("contratacion")}
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/50 px-7 py-3 text-[12px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                >
                  Contrata el espectáculo
                  <ArrowRight size={15} />
                </button>
              </div>
            </Reveal>
          )}

          {!loading &&
            !error &&
            concerts.map((c, i) => (
              <Reveal key={c.id || i} delay={Math.min(i * 0.05, 0.2)}>
                <div
                  data-testid={`concert-row-${i + 1}`}
                  className="group flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8 border-t border-cream/10 py-6 transition-all duration-300 hover:bg-cream/[0.02] hover:pl-3"
                >
                  <div className="flex items-baseline gap-3 sm:w-32 shrink-0">
                    <span className="font-serif text-4xl md:text-5xl text-gold leading-none">{c.day}</span>
                    <span className="text-[11px] uppercase tracking-[0.25em] text-sand">
                      {c.month} {c.year}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl md:text-2xl text-cream group-hover:text-gold transition-colors">{c.venue}</h3>
                    <p className="mt-1.5 flex items-center gap-2 text-sm text-sand">
                      <MapPin size={13} className="text-gold/70" />
                      {c.region !== c.city ? `${c.city} · ${c.region}` : c.city}
                    </p>
                  </div>
                  <span className={`self-start sm:self-center inline-flex items-center border px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] ${statusStyle(c.status)}`} data-testid={`concert-status-${i + 1}`}>
                    {c.status}
                  </span>
                  <button
                    data-testid={`concert-tickets-button-${i + 1}`}
                    onClick={() => onTickets(c)}
                    className="self-start sm:self-center inline-flex items-center gap-2 rounded-full bg-cream text-ink px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.18em] transition-all duration-300 hover:bg-gold"
                  >
                    <Ticket size={14} />
                    Entradas
                  </button>
                </div>
              </Reveal>
            ))}
          {!loading && !error && concerts.length > 0 && <div className="border-t border-cream/10" />}
        </div>
      </div>
    </section>
  );
};
